import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  CloseButton,
  Divider,
  Drawer,
  FileButton,
  Group,
  Indicator,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  Progress,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconChevronRight,
  IconDownload,
  IconEdit,
  IconExternalLink,
  IconFile,
  IconInbox,
  IconMessageCircle,
  IconPaperclip,
  IconPlus,
  IconReceipt,
  IconSearch,
  IconSend,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import {
  ATTACHMENT_CONTENT_TYPES,
  type AttachmentContentType,
  type ChatClaimSummary,
  type ConversationMessage,
  type ConversationSummary,
} from '@billing/validation';
import {
  createConversation,
  getConversationMessages,
  getConversations,
  getMessageAttachmentDownloadURL,
  markConversationRead,
  prepareMessageAttachments,
  searchConversationClaims,
  updateConversation,
} from 'api/resource/conversation';
import { useListUsersByOrganization } from 'api/resource/user';
import {
  listenForPresence,
  sendChatMessage,
  subscribeToChat,
} from 'api/ws/chat';
import { useRemoteFeatureFlag } from 'hooks/useRemoteFeatureFlag';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useIsProvider } from 'hooks/useIsProvider';
import { getUserNameWithTitlePrefix } from 'hooks/useGetUsersDisplayInfo';
import type { User } from 'interfaces';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useChatUIStore } from 'store/ChatStore';
import { useCurrentUserStore } from 'store/UserStore';
import { CHAT_LAYOUT, getChatColors, getChatStyles } from 'styles/ChatStyles';
import {
  formatUnreadCount,
  getUnreadIndicatorColor,
  UNREAD_INDICATOR_SIZE,
} from 'styles/UnreadIndicatorStyles';
import { uploadFileToSignedURL } from 'utils/fileUpload';

const claimStatusTranslationKeys = {
  Paid: 'paid',
  'Written-off': 'writtenOff',
  'Partially-paid': 'partiallyPaid',
  Submitted: 'submitted',
  'In-progress': 'inProgress',
  Cancelled: 'cancelled',
} as const;

const ChatClaimCard = ({
  claim,
  outgoing = false,
  onRemove,
  showOpenAction = true,
}: {
  claim: ChatClaimSummary;
  outgoing?: boolean;
  onRemove?: () => void;
  showOpenAction?: boolean;
}) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const status = claim.status
    ? translate(
        `common:chat.claimStatuses.${claimStatusTranslationKeys[claim.status]}`
      )
    : translate('common:chat.claimStatusUnavailable');
  const action = (() => {
    if (onRemove) {
      return (
        <CloseButton
          size="xs"
          onClick={onRemove}
          aria-label={translate('common:chat.removeClaim')}
        />
      );
    }
    if (!showOpenAction) return null;
    return (
      <ActionIcon
        variant="subtle"
        color={outgoing ? 'white' : 'blue'}
        size="sm"
        aria-label={translate('common:chat.openClaim')}
        onClick={() => navigate(`/claims/edit/${claim.id}`)}
      >
        <IconExternalLink size={14} />
      </ActionIcon>
    );
  })();
  return (
    <Paper
      withBorder={!outgoing}
      radius="md"
      p="xs"
      bg={outgoing ? 'rgba(255, 255, 255, 0.13)' : undefined}
    >
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <ThemeIcon variant={outgoing ? 'white' : 'light'} size="md">
          <IconReceipt size={16} />
        </ThemeIcon>
        <Box flex={1} miw={0}>
          <Text size="xs" fw={700} lineClamp={1}>
            {claim.claimNumber || translate('common:chat.unnumberedClaim')}
          </Text>
          <Text size="xs" opacity={0.78}>
            {DateTime.fromISO(claim.serviceDate).toLocaleString(
              DateTime.DATE_MED
            )}{' '}
            · {status}
          </Text>
        </Box>
        {action}
      </Group>
    </Paper>
  );
};

const displayName = (participant: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) =>
  `${participant.firstName ?? ''} ${participant.lastName ?? ''}`.trim() ||
  participant.email ||
  '';

const displayUserName = (user: User) =>
  getUserNameWithTitlePrefix(user) || user.email || '';

const getInitials = (participant: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) => {
  const initials = [participant.firstName, participant.lastName]
    .filter(Boolean)
    .map((value) => value?.charAt(0).toUpperCase())
    .join('');
  return initials || participant.email?.charAt(0).toUpperCase() || '?';
};

const isProviderAccount = (user: User): boolean =>
  Boolean(user.providerID || user.providers?.length) ||
  user.role?.name.toLocaleLowerCase() === 'provider';

const formatTimestamp = (timestamp: string, timezone: string): string => {
  const localTimestamp = DateTime.fromISO(timestamp, { zone: 'utc' }).setZone(
    timezone
  );
  const localNow = DateTime.now().setZone(timezone);
  if (localTimestamp.hasSame(localNow, 'day')) {
    return localTimestamp.toLocaleString(DateTime.TIME_SIMPLE);
  }
  return localTimestamp.toLocaleString({ month: 'short', day: 'numeric' });
};

const getAllConversations = async (): Promise<ConversationSummary[]> => {
  const conversations: ConversationSummary[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  do {
    const result = await getConversations(cursor);
    conversations.push(...result.data);
    cursor = result.nextCursor ?? undefined;
    if (cursor && seenCursors.has(cursor)) break;
    if (cursor) seenCursors.add(cursor);
  } while (cursor);

  return Array.from(
    new Map(
      conversations.map((conversation) => [conversation.id, conversation])
    ).values()
  );
};

export const ChatToolbarButton = () => {
  const enabled = useRemoteFeatureFlag('chat');
  const toggle = useChatUIStore((state) => state.toggle);
  const unreadCount = useChatUIStore((state) => state.unreadCount);
  const isAdmin = useIsAdmin();
  const { translate } = useTranslation();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  if (!enabled || isAdmin) return null;
  return (
    <Tooltip label={translate('common:chat.open')}>
      <Indicator
        label={formatUnreadCount(unreadCount)}
        size={UNREAD_INDICATOR_SIZE}
        color={getUnreadIndicatorColor(theme, colorScheme)}
        disabled={unreadCount === 0}
      >
        <ActionIcon
          variant="subtle"
          size="lg"
          onClick={toggle}
          aria-label={translate('common:chat.open')}
        >
          <IconMessageCircle size={22} />
        </ActionIcon>
      </Indicator>
    </Tooltip>
  );
};

export const ChatManager = () => {
  const enabled = useRemoteFeatureFlag('chat');
  const isAdmin = useIsAdmin();
  const isProvider = useIsProvider();
  const { translate } = useTranslation();
  const { opened, setOpened, setUnreadCount, stagedClaimID, clearStagedClaim } =
    useChatUIStore();
  const currentUser = useCurrentUserStore();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const compact =
    useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) ?? false;
  const chatStyles = useMemo(
    () => getChatStyles(theme, colorScheme, compact),
    [colorScheme, compact, theme]
  );
  const chatColors = useMemo(
    () => getChatColors(theme, colorScheme),
    [colorScheme, theme]
  );
  const timezone = useCurrentSiteStore((state) => state.timezone) || 'UTC';
  const { data: organizationUsers = [] } = useListUsersByOrganization({
    orgID: currentUser.organizationID ?? '',
  });
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedID, setSelectedID] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    Record<string, ConversationMessage[]>
  >({});
  const [messageCursor, setMessageCursor] = useState<
    Record<string, string | null>
  >({});
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [claim, setClaim] = useState<ChatClaimSummary | null>(null);
  const [claimPickerOpened, setClaimPickerOpened] = useState(false);
  const [claimQuery, setClaimQuery] = useState('');
  const [claimResults, setClaimResults] = useState<ChatClaimSummary[]>([]);
  const [claimSearchLoading, setClaimSearchLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [formOpened, setFormOpened] = useState(false);
  const [editing, setEditing] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [participantIDs, setParticipantIDs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [onlineUserIDs, setOnlineUserIDs] = useState<Set<string>>(new Set());
  const endRef = useRef<HTMLDivElement | null>(null);
  const messageViewportRef = useRef<HTMLDivElement | null>(null);
  const pendingHistoryScrollRef = useRef<{
    conversationID: string;
    scrollHeight: number;
  } | null>(null);

  const refresh = useCallback(async () => {
    if (
      !enabled ||
      isAdmin ||
      !currentUser.cognitoID ||
      !currentUser.organizationID
    )
      return;
    setListLoading(true);
    try {
      setConversations(await getAllConversations());
    } catch {
      setError(translate('common:chat.loadError'));
    } finally {
      setListLoading(false);
    }
  }, [
    currentUser.cognitoID,
    currentUser.organizationID,
    enabled,
    isAdmin,
    translate,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!stagedClaimID) return;
    setSelectedID(null);
    setClaim(null);
  }, [stagedClaimID]);

  useEffect(
    () =>
      subscribeToChat((event) => {
        if (event.action === 'chat.conversation.updated') {
          void refresh();
          return;
        }
        if (event.action === 'chat.message.rejected') return;
        const incoming = event.message;
        setMessages((current) => {
          const existing = current[incoming.conversationID] ?? [];
          const withoutOptimistic = existing.filter(
            (message) =>
              message.id !== incoming.clientMessageID &&
              message.id !== incoming.id
          );
          return {
            ...current,
            [incoming.conversationID]: [...withoutOptimistic, incoming].sort(
              (a, b) => a.createdAt.localeCompare(b.createdAt)
            ),
          };
        });
        if (incoming.conversationID === selectedID) {
          void (async () => {
            try {
              await markConversationRead(incoming.conversationID, incoming.id);
            } catch {
              setError(translate('common:chat.loadError'));
            } finally {
              await refresh();
            }
          })();
          return;
        }
        void refresh();
      }),
    [refresh, selectedID, translate]
  );

  useEffect(() => {
    if (!currentUser.cognitoID) return undefined;
    return listenForPresence(currentUser.cognitoID, (event) => {
      if (event.action === 'presence.snapshot') {
        setOnlineUserIDs(new Set(event.userIDs));
        return;
      }
      setOnlineUserIDs((current) => {
        const next = new Set(current);
        if (event.action === 'hello') next.add(event.userID);
        else next.delete(event.userID);
        return next;
      });
    });
  }, [currentUser.cognitoID]);

  const selected = conversations.find(({ id }) => id === selectedID);
  const selectedMessages = selectedID ? messages[selectedID] ?? [] : [];
  const organizationUserByCognitoID = useMemo(
    () =>
      new Map(
        organizationUsers.flatMap((user) =>
          user.cognitoID ? [[user.cognitoID, user] as const] : []
        )
      ),
    [organizationUsers]
  );
  const participantDisplayName = useCallback(
    (participant: {
      id: string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    }) => {
      const organizationUser = organizationUserByCognitoID.get(participant.id);
      return organizationUser
        ? displayUserName(organizationUser)
        : displayName(participant);
    },
    [organizationUserByCognitoID]
  );
  const selectableUsers = organizationUsers
    .filter(
      (user) =>
        user.cognitoID &&
        user.cognitoID !== currentUser.cognitoID &&
        user.status === 'active' &&
        (!isProvider || !isProviderAccount(user))
    )
    .map((user) => ({
      value: user.cognitoID!,
      label: displayUserName(user),
      user,
      online: onlineUserIDs.has(user.cognitoID!),
    }))
    .sort((first, second) => {
      if (first.online !== second.online) return first.online ? -1 : 1;
      return first.label.localeCompare(second.label);
    });
  const selectableUserByID = new Map(
    selectableUsers.map((option) => [option.value, option])
  );

  const titleFor = useCallback(
    (conversation: ConversationSummary) => {
      if (conversation.kind === 'group')
        return conversation.title || translate('common:chat.group');
      const other = conversation.participants.find(
        ({ id }) => id !== currentUser.cognitoID
      );
      return other
        ? participantDisplayName(other)
        : translate('common:chat.direct');
    },
    [currentUser.cognitoID, participantDisplayName, translate]
  );

  const validateStagedClaim = async (conversationID: string) => {
    if (!stagedClaimID) return;
    try {
      const result = await searchConversationClaims({
        conversationID,
        claimID: stagedClaimID,
        pageSize: 1,
      });
      const stagedClaim = result.data[0];
      if (!stagedClaim) throw new Error('Claim is unavailable.');
      setClaim(stagedClaim);
      clearStagedClaim();
    } catch {
      setClaim(null);
      setError(translate('common:chat.claimAccessError'));
    }
  };

  const openConversation = async (conversation: ConversationSummary) => {
    setSelectedID(conversation.id);
    setClaim(null);
    await validateStagedClaim(conversation.id);
    let latestMessage = messages[conversation.id]?.at(-1);
    if (!messages[conversation.id]) {
      setLoading(true);
      try {
        const result = await getConversationMessages(conversation.id);
        setMessages((current) => ({
          ...current,
          [conversation.id]: [...result.data].reverse(),
        }));
        setMessageCursor((current) => ({
          ...current,
          [conversation.id]: result.nextCursor,
        }));
        latestMessage = result.data[0];
      } finally {
        setLoading(false);
      }
    }
    if (latestMessage) {
      await markConversationRead(conversation.id, latestMessage.id);
      setConversations((current) =>
        current.map((item) =>
          item.id === conversation.id ? { ...item, unreadCount: 0 } : item
        )
      );
    }
  };

  const loadEarlier = async () => {
    if (!selectedID || !messageCursor[selectedID] || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const result = await getConversationMessages(
        selectedID,
        messageCursor[selectedID]!
      );
      pendingHistoryScrollRef.current = {
        conversationID: selectedID,
        scrollHeight: messageViewportRef.current?.scrollHeight ?? 0,
      };
      setMessages((current) => ({
        ...current,
        [selectedID]: [...result.data]
          .reverse()
          .concat(current[selectedID] ?? []),
      }));
      setMessageCursor((current) => ({
        ...current,
        [selectedID]: result.nextCursor,
      }));
    } catch {
      pendingHistoryScrollRef.current = null;
      setError(translate('common:chat.loadError'));
    } finally {
      setLoadingEarlier(false);
    }
  };

  useLayoutEffect(() => {
    const pendingHistoryScroll = pendingHistoryScrollRef.current;
    const viewport = messageViewportRef.current;
    if (pendingHistoryScroll?.conversationID === selectedID && viewport) {
      viewport.scrollTop +=
        viewport.scrollHeight - pendingHistoryScroll.scrollHeight;
      pendingHistoryScrollRef.current = null;
      return;
    }
    pendingHistoryScrollRef.current = null;
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedID, selectedMessages.length]);

  const submitConversation = async () => {
    setError(null);
    try {
      if (editing && selected) {
        await updateConversation({
          conversationID: selected.id,
          title: groupTitle,
          participantIDs: Array.from(
            new Set([currentUser.cognitoID!, ...participantIDs])
          ),
        });
      } else {
        const kind = participantIDs.length === 1 ? 'direct' : 'group';
        const conversationID = await createConversation({
          kind,
          title: kind === 'group' ? groupTitle : undefined,
          participantIDs,
        });
        setSelectedID(conversationID);
        setClaim(null);
        await validateStagedClaim(conversationID);
      }
      setFormOpened(false);
      await refresh();
    } catch {
      setError(translate('common:chat.error'));
    }
  };

  useEffect(() => {
    if (!claimPickerOpened || !selectedID) return undefined;
    const timeoutID = window.setTimeout(() => {
      setClaimSearchLoading(true);
      void searchConversationClaims({
        conversationID: selectedID,
        query: claimQuery,
        pageSize: 20,
      })
        .then((result) => setClaimResults(result.data))
        .catch(() => setError(translate('common:chat.claimSearchError')))
        .finally(() => setClaimSearchLoading(false));
    }, 250);
    return () => window.clearTimeout(timeoutID);
  }, [claimPickerOpened, claimQuery, selectedID, translate]);

  const send = async () => {
    if (
      !selectedID ||
      sending ||
      (!draft.trim() && files.length === 0 && !claim)
    )
      return;
    setSending(true);
    setError(null);
    const clientMessageID = uuidv4();
    try {
      const targets = files.length
        ? await prepareMessageAttachments(selectedID, files)
        : [];
      const progress = targets.map(() => 0);
      await Promise.all(
        targets.map((target, index) =>
          uploadFileToSignedURL({
            signedURL: target.uploadURL,
            data: files[index],
            contentType: files[index].type,
            onProgress: (percentage) => {
              progress[index] = percentage;
              setUploadProgress(
                Math.round(
                  progress.reduce((total, value) => total + value, 0) /
                    progress.length
                )
              );
            },
          })
        )
      );
      const optimistic: ConversationMessage = {
        id: clientMessageID,
        clientMessageID,
        conversationID: selectedID,
        senderID: currentUser.cognitoID!,
        content: draft.trim(),
        createdAt: DateTime.utc().toISO()!,
        attachments: targets.map((target) => ({
          id: target.id,
          originalFilename: target.originalFilename,
        })),
        claimReference: claim ? { available: true, claim } : null,
      };
      setMessages((current) => ({
        ...current,
        [selectedID]: [...(current[selectedID] ?? []), optimistic],
      }));
      await sendChatMessage({
        clientMessageID,
        conversationID: selectedID,
        content: draft.trim(),
        attachmentIDs: targets.map(({ id }) => id),
        claimID: claim?.id ?? null,
      });
      setDraft('');
      setFiles([]);
      setClaim(null);
    } catch {
      setMessages((current) => ({
        ...current,
        [selectedID]: (current[selectedID] ?? []).filter(
          ({ id }) => id !== clientMessageID
        ),
      }));
      setError(translate('common:chat.sendError'));
    } finally {
      setSending(false);
      setUploadProgress(null);
    }
  };

  const totalUnread = useMemo(
    () =>
      conversations.reduce(
        (total, conversation) => total + conversation.unreadCount,
        0
      ),
    [conversations]
  );
  useEffect(() => {
    setUnreadCount(totalUnread);
  }, [setUnreadCount, totalUnread]);
  if (
    !enabled ||
    isAdmin ||
    !currentUser.cognitoID ||
    !currentUser.organizationID
  )
    return null;

  const selectedOtherParticipant = selected?.participants.find(
    ({ id }) => id !== currentUser.cognitoID
  );
  const selectedOtherIsOnline = Boolean(
    selected?.kind === 'direct' &&
      selectedOtherParticipant &&
      onlineUserIDs.has(selectedOtherParticipant.id)
  );
  const selectedMeta = (() => {
    if (!selected) return '';
    if (selected.kind === 'group') {
      return translate('common:chat.memberCount', {
        count: selected.participants.length,
      });
    }
    const statusKey = selectedOtherIsOnline
      ? 'common:chat.online'
      : 'common:chat.offline';
    return translate(statusKey);
  })();
  const selectedAvatarText = (() => {
    if (!selected) return '';
    if (selected.kind === 'group') {
      return selected.title?.charAt(0).toUpperCase() ?? 'G';
    }
    return getInitials(selectedOtherParticipant ?? {});
  })();
  const canSend =
    selected?.isActiveMember && Boolean(draft.trim() || files.length || claim);
  const drawerSize = (() => {
    if (compact) return CHAT_LAYOUT.drawerWidth.compact;
    return selected
      ? CHAT_LAYOUT.drawerWidth.conversation
      : CHAT_LAYOUT.drawerWidth.inbox;
  })();

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        size={drawerSize}
        padding={0}
        overlayProps={CHAT_LAYOUT.overlay}
        styles={{
          content: chatStyles.drawerContent,
          header: chatStyles.drawerHeader,
          title: chatStyles.drawerTitle,
          body: chatStyles.drawerBody,
        }}
        title={
          selected ? (
            <Group gap="sm" wrap="nowrap">
              <Indicator
                color={selectedOtherIsOnline ? chatColors.online : 'gray'}
                size={10}
                offset={4}
                disabled={selected.kind === 'group'}
                withBorder
              >
                <Avatar color={chatColors.primary} radius="xl" size={40}>
                  {selectedAvatarText}
                </Avatar>
              </Indicator>
              <Box>
                <Text fw={700} size="sm" lineClamp={1}>
                  {titleFor(selected)}
                </Text>
                <Text size="xs" c="dimmed" fw={500}>
                  {selectedMeta}
                </Text>
              </Box>
            </Group>
          ) : (
            <Group gap="sm">
              <ThemeIcon variant="light" radius="md" size={38}>
                <IconMessageCircle size={20} stroke={1.8} />
              </ThemeIcon>
              <Box>
                <Text fw={700}>{translate('common:chat.messages')}</Text>
                <Text size="xs" c="dimmed" fw={400}>
                  {translate('common:chat.subtitle')}
                </Text>
              </Box>
            </Group>
          )
        }
      >
        {selected ? (
          <Stack style={chatStyles.fullHeightView} gap={0}>
            <Group justify="space-between" style={chatStyles.conversationNav}>
              <Button
                variant="subtle"
                color="gray"
                size="compact-sm"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => setSelectedID(null)}
              >
                {translate('common:chat.back')}
              </Button>
              {selected.kind === 'group' &&
                selected.creatorID === currentUser.cognitoID && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={translate('common:chat.editGroup')}
                    onClick={() => {
                      setEditing(true);
                      setGroupTitle(selected.title ?? '');
                      setParticipantIDs(
                        selected.participants
                          .map(({ id }) => id)
                          .filter((id) => id !== currentUser.cognitoID)
                      );
                      setFormOpened(true);
                    }}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
            </Group>
            {!selected.isActiveMember && (
              <Alert
                color="gray"
                variant="light"
                icon={<IconAlertCircle size={17} />}
                style={chatStyles.inlineAlert}
              >
                {translate('common:chat.readOnly')}
              </Alert>
            )}
            <ScrollArea
              flex={1}
              style={chatStyles.messageScroll}
              viewportRef={messageViewportRef}
            >
              <Stack gap="md" style={chatStyles.messageList}>
                {messageCursor[selected.id] && (
                  <Center>
                    <Button
                      variant="light"
                      color="gray"
                      size="compact-xs"
                      radius="xl"
                      loading={loadingEarlier}
                      onClick={() => void loadEarlier()}
                    >
                      {translate('common:chat.loadEarlier')}
                    </Button>
                  </Center>
                )}
                {loading && (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                )}
                {!loading && selectedMessages.length === 0 && (
                  <Center style={chatStyles.emptyMessages}>
                    <Stack align="center" gap={6}>
                      <ThemeIcon variant="light" radius="xl" size={46}>
                        <IconMessageCircle size={23} stroke={1.6} />
                      </ThemeIcon>
                      <Text fw={650} size="sm">
                        {translate('common:chat.noMessages')}
                      </Text>
                      <Text c="dimmed" size="xs" ta="center" maw={260}>
                        {translate('common:chat.noMessagesDescription')}
                      </Text>
                    </Stack>
                  </Center>
                )}
                {selectedMessages.map((message) => {
                  const outgoing = message.senderID === currentUser.cognitoID;
                  const sender = selected.participants.find(
                    ({ id }) => id === message.senderID
                  );
                  return (
                    <Group
                      key={message.id}
                      align="flex-end"
                      gap="xs"
                      wrap="nowrap"
                      justify={outgoing ? 'flex-end' : 'flex-start'}
                    >
                      {!outgoing && (
                        <Avatar color="gray" radius="xl" size={28}>
                          {getInitials(sender ?? {})}
                        </Avatar>
                      )}
                      <Box style={chatStyles.messageColumn}>
                        {selected.kind === 'group' && !outgoing && (
                          <Text size="xs" fw={650} c="dimmed" mb={4} ml={4}>
                            {sender ? participantDisplayName(sender) : ''}
                          </Text>
                        )}
                        <Paper
                          style={
                            outgoing
                              ? chatStyles.outgoingMessage
                              : chatStyles.incomingMessage
                          }
                          px="sm"
                          py="xs"
                          radius="lg"
                        >
                          {message.content && (
                            <Text size="sm" style={chatStyles.messageText}>
                              {message.content}
                            </Text>
                          )}
                          {message.claimReference && (
                            <Box mt={message.content ? 7 : 0}>
                              {message.claimReference.available ? (
                                <ChatClaimCard
                                  claim={message.claimReference.claim}
                                  outgoing={outgoing}
                                />
                              ) : (
                                <Text size="xs" fs="italic" opacity={0.78}>
                                  {translate(
                                    'common:chat.claimReferenceUnavailable'
                                  )}
                                </Text>
                              )}
                            </Box>
                          )}
                          {message.attachments.length > 0 && (
                            <Stack
                              gap={4}
                              mt={
                                message.content || message.claimReference
                                  ? 7
                                  : 0
                              }
                            >
                              {message.attachments.map((attachment) => (
                                <UnstyledButton
                                  key={attachment.id}
                                  style={
                                    outgoing
                                      ? chatStyles.outgoingAttachment
                                      : chatStyles.incomingAttachment
                                  }
                                  onClick={async () => {
                                    try {
                                      window.open(
                                        await getMessageAttachmentDownloadURL(
                                          attachment.id
                                        ),
                                        '_blank',
                                        'noopener,noreferrer'
                                      );
                                    } catch {
                                      setError(
                                        translate('common:chat.downloadError')
                                      );
                                    }
                                  }}
                                >
                                  <IconFile size={15} />
                                  <Text size="xs" fw={600} lineClamp={1}>
                                    {attachment.originalFilename}
                                  </Text>
                                  <IconDownload size={14} />
                                </UnstyledButton>
                              ))}
                            </Stack>
                          )}
                          <Text
                            size="xs"
                            ta="right"
                            style={chatStyles.messageTime}
                          >
                            {formatTimestamp(message.createdAt, timezone)}
                          </Text>
                        </Paper>
                      </Box>
                    </Group>
                  );
                })}
                <div ref={endRef} />
              </Stack>
            </ScrollArea>
            <Box style={chatStyles.composerArea}>
              {error && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertCircle size={16} />}
                  withCloseButton
                  onClose={() => setError(null)}
                  mb="sm"
                >
                  {error}
                </Alert>
              )}
              {claim && (
                <Box mb="xs">
                  <ChatClaimCard
                    claim={claim}
                    onRemove={() => setClaim(null)}
                  />
                </Box>
              )}
              {files.length > 0 && (
                <Group gap={6} mb="xs">
                  {files.map((file, index) => (
                    <Paper
                      key={`${file.name}-${file.size}-${index}`}
                      withBorder
                      style={chatStyles.queuedAttachment}
                    >
                      <IconFile size={14} />
                      <Text size="xs" fw={600} lineClamp={1}>
                        {file.name}
                      </Text>
                      <CloseButton
                        size="xs"
                        aria-label={translate('common:chat.removeAttachment', {
                          filename: file.name,
                        })}
                        onClick={() =>
                          setFiles((current) =>
                            current.filter(
                              (_, fileIndex) => fileIndex !== index
                            )
                          )
                        }
                      />
                    </Paper>
                  ))}
                </Group>
              )}
              {uploadProgress !== null && (
                <Box mb="xs">
                  <Group justify="space-between" mb={4}>
                    <Text size="xs" c="dimmed" fw={500}>
                      {translate('common:chat.uploading', {
                        progress: uploadProgress,
                      })}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {uploadProgress}%
                    </Text>
                  </Group>
                  <Progress value={uploadProgress} size="xs" animated />
                </Box>
              )}
              <Paper withBorder radius="lg" style={chatStyles.composer}>
                <Textarea
                  flex={1}
                  variant="unstyled"
                  autosize
                  minRows={1}
                  maxRows={4}
                  value={draft}
                  maxLength={4000}
                  disabled={!selected.isActiveMember}
                  placeholder={translate('common:chat.placeholder')}
                  onChange={(event) => setDraft(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                  }}
                />
                <Group gap={4} align="flex-end" wrap="nowrap">
                  <Tooltip label={translate('common:chat.attachClaim')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={!selected.isActiveMember || Boolean(claim)}
                      aria-label={translate('common:chat.attachClaim')}
                      onClick={() => {
                        setClaimQuery('');
                        setClaimPickerOpened(true);
                      }}
                    >
                      <IconReceipt size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <FileButton
                    onChange={(nextFiles) => {
                      const selectedFiles = nextFiles ?? [];
                      const valid = selectedFiles.filter(
                        (file) =>
                          file.size <= 10 * 1024 * 1024 &&
                          ATTACHMENT_CONTENT_TYPES.includes(
                            file.type as AttachmentContentType
                          )
                      );
                      if (
                        selectedFiles.length > 5 ||
                        valid.length !== selectedFiles.length
                      ) {
                        setError(translate('common:chat.attachmentLimit'));
                      }
                      setFiles(valid.slice(0, 5));
                    }}
                    accept="application/pdf,text/csv,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,image/gif,image/webp,image/tiff,image/bmp,image/heic,image/heif"
                    multiple
                  >
                    {(props) => (
                      <Tooltip label={translate('common:chat.attach')}>
                        <ActionIcon
                          {...props}
                          variant="subtle"
                          color="gray"
                          disabled={!selected.isActiveMember}
                          aria-label={translate('common:chat.attach')}
                        >
                          <IconPaperclip size={18} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </FileButton>
                  <Tooltip label={translate('common:chat.send')}>
                    <ActionIcon
                      size="lg"
                      radius="xl"
                      loading={sending}
                      disabled={!canSend}
                      onClick={() => void send()}
                      aria-label={translate('common:chat.send')}
                    >
                      <IconSend size={17} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Paper>
              <Text size="xs" c="dimmed" ta="center" mt={6}>
                {translate('common:chat.sendHint')}
              </Text>
            </Box>
          </Stack>
        ) : (
          <Stack style={chatStyles.fullHeightView} gap={0}>
            <Group justify="space-between" style={chatStyles.inboxToolbar}>
              <Box>
                <Text fw={700} size="sm">
                  {totalUnread > 0
                    ? translate('common:chat.unread', { count: totalUnread })
                    : translate('common:chat.allCaughtUp')}
                </Text>
                <Text size="xs" c="dimmed">
                  {translate('common:chat.inboxDescription')}
                </Text>
              </Box>
              <Button
                radius="xl"
                size="sm"
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setEditing(false);
                  setGroupTitle('');
                  setParticipantIDs([]);
                  setFormOpened(true);
                }}
              >
                {translate('common:chat.new')}
              </Button>
            </Group>
            <Divider />
            <ScrollArea flex={1}>
              {listLoading && conversations.length === 0 && (
                <Stack p="md" gap="md">
                  {[0, 1, 2].map((item) => (
                    <Group key={item} wrap="nowrap">
                      <Skeleton circle h={44} />
                      <Box flex={1}>
                        <Skeleton h={10} w="45%" mb="xs" radius="xl" />
                        <Skeleton h={8} w="85%" radius="xl" />
                      </Box>
                    </Group>
                  ))}
                </Stack>
              )}
              {!listLoading && conversations.length === 0 && (
                <Center style={chatStyles.emptyInbox}>
                  <Stack align="center" gap="sm">
                    <ThemeIcon
                      variant="light"
                      radius="xl"
                      size={64}
                      color={chatColors.primary}
                    >
                      <IconInbox size={30} stroke={1.5} />
                    </ThemeIcon>
                    <Box ta="center">
                      <Text fw={700}>{translate('common:chat.empty')}</Text>
                      <Text c="dimmed" size="sm" mt={4} maw={280}>
                        {translate('common:chat.emptyDescription')}
                      </Text>
                    </Box>
                    <Button
                      variant="light"
                      radius="xl"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => {
                        setEditing(false);
                        setGroupTitle('');
                        setParticipantIDs([]);
                        setFormOpened(true);
                      }}
                    >
                      {translate('common:chat.startConversation')}
                    </Button>
                  </Stack>
                </Center>
              )}
              {conversations.map((conversation) => {
                const otherParticipant = conversation.participants.find(
                  ({ id }) => id !== currentUser.cognitoID
                );
                const online = conversation.participants.some(
                  ({ id }) =>
                    id !== currentUser.cognitoID && onlineUserIDs.has(id)
                );
                const latestMessage = conversation.latestMessage;
                const availableClaim =
                  latestMessage?.claimReference?.available === true
                    ? latestMessage.claimReference.claim
                    : null;
                const claimPreview = (() => {
                  if (availableClaim) {
                    return translate('common:chat.claimPreview', {
                      claimNumber:
                        availableClaim.claimNumber ||
                        translate('common:chat.unnumberedClaim'),
                    });
                  }
                  if (latestMessage?.claimReference) {
                    return translate('common:chat.claimReferenceUnavailable');
                  }
                  return '';
                })();
                const preview =
                  latestMessage?.content ||
                  latestMessage?.attachments[0]?.originalFilename ||
                  claimPreview ||
                  translate('common:chat.noMessagesPreview');
                const avatarText =
                  conversation.kind === 'group'
                    ? conversation.title?.charAt(0).toUpperCase() ?? 'G'
                    : getInitials(otherParticipant ?? {});
                return (
                  <UnstyledButton
                    key={conversation.id}
                    style={chatStyles.conversationRow}
                    onClick={() => void openConversation(conversation)}
                  >
                    <Indicator
                      color={chatColors.online}
                      size={10}
                      offset={4}
                      disabled={!online}
                      withBorder
                    >
                      <Avatar color={chatColors.primary} radius="xl" size={44}>
                        {conversation.kind === 'group' ? (
                          <IconUsers size={20} />
                        ) : (
                          avatarText
                        )}
                      </Avatar>
                    </Indicator>
                    <Box style={chatStyles.conversationSummary}>
                      <Group justify="space-between" gap="xs" wrap="nowrap">
                        <Text fw={650} size="sm" lineClamp={1}>
                          {titleFor(conversation)}
                        </Text>
                        {conversation.latestMessage && (
                          <Text size="xs" c="dimmed" style={chatStyles.rowTime}>
                            {formatTimestamp(
                              conversation.latestMessage.createdAt,
                              timezone
                            )}
                          </Text>
                        )}
                      </Group>
                      <Group justify="space-between" gap="xs" wrap="nowrap">
                        <Text size="xs" c="dimmed" lineClamp={1} flex={1}>
                          {preview}
                        </Text>
                        {conversation.unreadCount > 0 ? (
                          <Badge size="sm" circle>
                            {conversation.unreadCount}
                          </Badge>
                        ) : (
                          <IconChevronRight
                            size={16}
                            style={chatStyles.rowChevron}
                          />
                        )}
                      </Group>
                    </Box>
                  </UnstyledButton>
                );
              })}
            </ScrollArea>
            {error && (
              <Alert
                m="md"
                mt={0}
                color="red"
                variant="light"
                icon={<IconAlertCircle size={16} />}
                withCloseButton
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </Drawer>

      <Modal
        opened={formOpened}
        onClose={() => setFormOpened(false)}
        radius="lg"
        centered
        title={
          editing
            ? translate('common:chat.editGroup')
            : translate('common:chat.new')
        }
      >
        <Stack gap="md">
          <Text c="dimmed" size="sm">
            {translate('common:chat.newDescription')}
          </Text>
          <TextInput
            label={translate('common:chat.groupTitle')}
            placeholder={translate('common:chat.groupTitlePlaceholder')}
            value={groupTitle}
            onChange={(event) => setGroupTitle(event.currentTarget.value)}
          />
          <MultiSelect
            label={translate('common:chat.participants')}
            placeholder={translate('common:chat.participantsPlaceholder')}
            data={selectableUsers.map(({ value, label }) => ({ value, label }))}
            value={participantIDs}
            searchable
            clearable
            onChange={setParticipantIDs}
            renderOption={({ option }) => {
              const selectableUser = selectableUserByID.get(option.value);
              const online = selectableUser?.online ?? false;
              return (
                <Group gap="sm" wrap="nowrap">
                  <Indicator
                    color={online ? chatColors.online : 'gray'}
                    size={9}
                    offset={3}
                    withBorder
                  >
                    <Avatar color={chatColors.primary} radius="xl" size={32}>
                      {getInitials(selectableUser?.user ?? {})}
                    </Avatar>
                  </Indicator>
                  <Box>
                    <Text size="sm" fw={600}>
                      {option.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(
                        online ? 'common:chat.online' : 'common:chat.offline'
                      )}
                    </Text>
                  </Box>
                </Group>
              );
            }}
          />
          {error && (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertCircle size={16} />}
            >
              {error}
            </Alert>
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setFormOpened(false)}>
              {translate('common:buttons.cancel')}
            </Button>
            <Button
              disabled={
                participantIDs.length === 0 ||
                (participantIDs.length > 1 && !groupTitle.trim())
              }
              onClick={() => void submitConversation()}
            >
              {translate('common:buttons.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={claimPickerOpened}
        onClose={() => setClaimPickerOpened(false)}
        radius="lg"
        centered
        title={translate('common:chat.claimPickerTitle')}
      >
        <Stack gap="sm">
          <TextInput
            value={claimQuery}
            onChange={(event) => setClaimQuery(event.currentTarget.value)}
            placeholder={translate('common:chat.claimSearchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            autoFocus
          />
          <Text size="xs" c="dimmed">
            {translate('common:chat.claimPickerDescription')}
          </Text>
          <ScrollArea.Autosize mah={360}>
            <Stack gap="xs">
              {claimSearchLoading && (
                <Center py="lg">
                  <Loader size="sm" />
                </Center>
              )}
              {!claimSearchLoading && claimResults.length === 0 && (
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  {translate('common:chat.noClaimsFound')}
                </Text>
              )}
              {!claimSearchLoading &&
                claimResults.map((result) => (
                  <UnstyledButton
                    key={result.id}
                    onClick={() => {
                      setClaim(result);
                      setClaimPickerOpened(false);
                    }}
                  >
                    <ChatClaimCard claim={result} showOpenAction={false} />
                  </UnstyledButton>
                ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Modal>
    </>
  );
};
